import { TranslateService } from '@ngx-translate/core';
import {
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  MenuItemDef,
} from 'ag-grid-community';
import { isRowDragColumn } from './ag-grid-row-interactions';

export type RowInsertPosition = 'above' | 'below';

/**
 * Actions the row context menu can request. Add one union member per new action.
 * `anchors` are the rows the action applies to: the drag-selected rows, the
 * checkbox-selected rows, or just the right-clicked row (see resolveAnchors).
 * Consumers must map them to their own source data, never to displayed indexes.
 */
export type RowContextMenuAction<T> = {
  readonly type: 'insert-row';
  readonly position: RowInsertPosition;
  readonly anchors: readonly T[];
};

/** Opt-in switches per menu action. */
export interface RowContextMenuConfig {
  readonly insertRows?: boolean;
  /** Shows the insert items disabled, e.g. while inserted rows are unsaved. */
  readonly insertRowsDisabled?: boolean;
}

interface RowContextMenuContext<T> {
  readonly api: GridApi<T>;
  readonly anchors: readonly T[];
  readonly config: RowContextMenuConfig;
  readonly translate: TranslateService;
  readonly emit: (action: RowContextMenuAction<T>) => void;
}

const ROW_CONTEXT_MENU_ITEM_CLASS = 'row-context-menu-item';

const INSERT_ROW_ICONS: Readonly<Record<RowInsertPosition, string>> = {
  above: '<img src="images/menu-arrow-up.svg" alt="" width="24" height="24" />',
  below:
    '<img src="images/menu-arrow-down.svg" alt="" width="24" height="24" />',
};

const INSERT_ROW_LABEL_KEYS: Readonly<
  Record<
    RowInsertPosition,
    { readonly single: string; readonly multiple: string }
  >
> = {
  above: {
    single: 'AG_GRID.CONTEXT_MENU.INSERT_ROW_ABOVE',
    multiple: 'AG_GRID.CONTEXT_MENU.INSERT_ROWS_ABOVE',
  },
  below: {
    single: 'AG_GRID.CONTEXT_MENU.INSERT_ROW_BELOW',
    multiple: 'AG_GRID.CONTEXT_MENU.INSERT_ROWS_BELOW',
  },
};

/** After an insert, checkbox selections and cell ranges point at rows that have moved. */
function clearSelections<T>(api: GridApi<T>): void {
  api.deselectAll();
  if (api.getCellRanges()?.length) {
    api.clearCellSelection();
  }
}

function buildInsertRowItem<T>(
  context: RowContextMenuContext<T>,
  position: RowInsertPosition
): MenuItemDef<T> {
  const { api, anchors, config, translate, emit } = context;
  const count = anchors.length;
  const keys = INSERT_ROW_LABEL_KEYS[position];

  return {
    name:
      count === 1
        ? translate.instant(keys.single)
        : translate.instant(keys.multiple, { count }),
    icon: INSERT_ROW_ICONS[position],
    disabled: config.insertRowsDisabled === true,
    cssClasses: [ROW_CONTEXT_MENU_ITEM_CLASS],
    action: () => {
      emit({ type: 'insert-row', position, anchors });
      clearSelections(api);
    },
  };
}

/**
 * Rows covered by a multi-row cell range that contains the right-clicked cell.
 * Empty when there is no such range. Range row indexes are displayed indexes,
 * so rows are resolved via the grid API.
 */
function getDragSelectedRows<T>(params: GetContextMenuItemsParams<T>): T[] {
  const { api, node, column } = params;
  const clickedRowIndex = node?.rowIndex;
  if (!column || clickedRowIndex === null || clickedRowIndex === undefined) {
    return [];
  }

  for (const range of api.getCellRanges() ?? []) {
    const { startRow, endRow } = range;
    if (!startRow || !endRow || startRow.rowPinned || endRow.rowPinned) {
      continue;
    }

    const first = Math.min(startRow.rowIndex, endRow.rowIndex);
    const last = Math.max(startRow.rowIndex, endRow.rowIndex);
    const containsClickedCell =
      clickedRowIndex >= first &&
      clickedRowIndex <= last &&
      range.columns.some((col) => col.getColId() === column.getColId());

    // A one-row range is just a focused cell, not a row selection.
    if (!containsClickedCell || first === last) {
      continue;
    }

    const rows: T[] = [];
    for (let index = first; index <= last; index++) {
      const data = api.getDisplayedRowAtIndex(index)?.data;
      if (data !== undefined) {
        rows.push(data);
      }
    }
    return rows;
  }

  return [];
}

/** Checkbox-selected rows, but only when the right-clicked row is one of them. */
function getCheckedRows<T>(params: GetContextMenuItemsParams<T>): T[] {
  return params.node?.isSelected() ? params.api.getSelectedRows() : [];
}

/** Drag range first, then checked rows, then just the right-clicked row. */
function resolveAnchors<T>(
  params: GetContextMenuItemsParams<T>,
  clickedRow: T
): readonly T[] {
  const dragSelectedRows = getDragSelectedRows(params);
  if (dragSelectedRows.length > 0) {
    return dragSelectedRows;
  }

  const checkedRows = getCheckedRows(params);
  return checkedRows.length > 0 ? checkedRows : [clickedRow];
}

/** Builds the menu for a right-clicked body row. No items for pinned rows or empty grid area. */
export function buildRowContextMenuItems<T>(
  params: GetContextMenuItemsParams<T>,
  config: RowContextMenuConfig,
  translate: TranslateService,
  emit: (action: RowContextMenuAction<T>) => void
): MenuItemDef<T>[] {
  const node = params.node;
  const clickedRow = node?.data;
  if (
    !node ||
    node.rowPinned ||
    clickedRow === undefined ||
    isRowDragColumn(params.column)
  ) {
    return [];
  }

  const context: RowContextMenuContext<T> = {
    api: params.api,
    anchors: resolveAnchors(params, clickedRow),
    config,
    translate,
    emit,
  };

  return config.insertRows
    ? [
        buildInsertRowItem(context, 'above'),
        buildInsertRowItem(context, 'below'),
      ]
    : [];
}

/** Returns a copy of the grid options with the shared row context menu applied. */
export function withRowContextMenu<T>(
  options: GridOptions<T>,
  config: RowContextMenuConfig,
  translate: TranslateService,
  emit: (action: RowContextMenuAction<T>) => void
): GridOptions<T> {
  return {
    ...options,
    getContextMenuItems: (params) =>
      buildRowContextMenuItems(params, config, translate, emit),
  };
}
